/**
 * Money and quantities (docs/adr/ADR-001): amounts are `numeric` in the
 * database and travel as strings, never as floats. Calculations work on whole
 * cents, so a total is always the sum of what the user reads.
 *
 * Every number the user reads is Italian, with the thousands dot even on four
 * digits: «1.234,56». What the user types uses the dot or the comma for the
 * decimals, never for the thousands: «15000.35» and «15000,35» are the same
 * (decision of 26/09/2026).
 */
/**
 * A number as typed, pasted or read from the database, in JavaScript form
 * ("15000.35"), or null when it is not a number.
 * - one separator, dot or comma, is the decimal one: "15000.35", "15000,35", "15.5";
 * - with both, the last one is the decimal one: "15.000,35" (Italian), "15,000.35";
 * - the same separator more than once groups thousands: "1.500.000".
 */
export function normaliseDecimal(value) {
    // `\s` also covers the non-breaking spaces of «1.234,50 €».
    const text = value.replace(/[\s€%]/g, '');
    const match = /^([-+]?)([\d.,]+)$/.exec(text);
    if (!match)
        return null;
    const [, sign, body] = match;
    const dots = body.split('.').length - 1;
    const commas = body.split(',').length - 1;
    let digits;
    if (dots > 0 && commas > 0) {
        const decimal = body.lastIndexOf('.') > body.lastIndexOf(',') ? '.' : ',';
        const group = decimal === '.' ? ',' : '.';
        const [whole, fraction] = [
            body.slice(0, body.lastIndexOf(decimal)),
            body.slice(body.lastIndexOf(decimal) + 1),
        ];
        if (fraction.includes(group) || !isGrouped(whole, group))
            return null;
        digits = `${whole.split(group).join('')}.${fraction}`;
    }
    else if (dots + commas === 1) {
        digits = body.replace(',', '.');
    }
    else if (dots + commas > 1) {
        const group = dots > 0 ? '.' : ',';
        if (!isGrouped(body, group))
            return null;
        digits = body.split(group).join('');
    }
    else {
        digits = body;
    }
    if (!/^(\d+\.?\d*|\.\d+)$/.test(digits))
        return null;
    return `${sign === '-' ? '-' : ''}${digits}`;
}
/** "1.500.000": groups of three after the first. */
function isGrouped(text, group) {
    const [first, ...rest] = text.split(group);
    return /^\d{1,3}$/.test(first ?? '') && rest.every((part) => /^\d{3}$/.test(part));
}
function toScaled(value, scale, error) {
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error(error);
        return Math.round(value * scale);
    }
    if (!value.trim())
        return 0;
    const normalised = normaliseDecimal(value);
    if (normalised === null)
        throw new Error(error);
    return Math.round(Number(normalised) * scale);
}
/** "15000,50", "15000.5", "1.234,50", 1234.5 → cents. Throws on what is not a number. */
export function toCents(value) {
    return toScaled(value, 100, 'Importo non valido');
}
export function fromCents(cents) {
    return (cents / 100).toFixed(2);
}
/** Quantities keep three decimals: "2,5" hours, "1000" pieces. */
export function toThousandths(value) {
    return toScaled(value, 1000, 'Quantità non valida');
}
/** Percentage with two decimals, as a whole number of hundredths: "12,5" → 1250. */
export function toHundredths(value) {
    return toScaled(value, 100, 'Percentuale non valida');
}
/** A number of any kind (tables, imports), or null when it is not one. */
export function parseDecimal(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    const normalised = normaliseDecimal(value);
    return normalised === null ? null : Number(normalised);
}
/* --- Display ------------------------------------------------------------------- */
/** Keeps the number and its symbol on the same line. */
const NBSP = String.fromCharCode(0xa0);
/**
 * Italian digits of a number, rounded to `maxDecimals` and keeping at least
 * `minDecimals`: «1.234,5». `grouping: false` leaves out the thousands dot, as
 * a field shows the number while it is being changed.
 */
export function formatDecimal(value, minDecimals, maxDecimals, grouping = true) {
    const fixed = Math.abs(value).toFixed(maxDecimals);
    let [whole = '0', fraction = ''] = fixed.split('.');
    while (fraction.length > minDecimals && fraction.endsWith('0'))
        fraction = fraction.slice(0, -1);
    if (grouping)
        whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const negative = value < 0 && Number(fixed) !== 0;
    return `${negative ? '-' : ''}${whole}${fraction ? `,${fraction}` : ''}`;
}
/** «1.234,50 €». */
export function formatMoney(value) {
    return `${formatDecimal(toCents(value) / 100, 2, 2)}${NBSP}€`;
}
/** «1.500», «2,5»: up to three decimals. */
export function formatQuantity(value) {
    return formatDecimal(toThousandths(value) / 1000, 0, 3);
}
/** «12,5 %». */
export function formatPercent(value) {
    return `${formatDecimal(toHundredths(value) / 100, 0, 2)}${NBSP}%`;
}
/** Any number, up to `maxDecimals` decimals: «1.234,5678». */
export function formatNumber(value, maxDecimals = 4) {
    const number = parseDecimal(value);
    return number === null ? String(value) : formatDecimal(number, 0, maxDecimals);
}
//# sourceMappingURL=money.js.map